import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { FaFileExport, FaCalendarAlt } from 'react-icons/fa';

/**
 * Component for displaying implementation roadmap data as a Gantt chart
 * @param {Object} props - Component props
 * @param {Array} props.recommendations - Array of recommendation objects to display
 * @param {string} props.organizationName - Name of the organization
 */
const RoadmapGanttChart = ({ recommendations, organizationName }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ganttData, setGanttData] = useState([]);
  const [timeRange, setTimeRange] = useState({
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 6))
  });
  const [showCompleted, setShowCompleted] = useState(true);
  const chartRef = useRef(null);

  // Colors from our color scheme
  const colors = {
    primary: '#243444',
    secondary: '#D2BA7B',
    light: '#FFFAFA',
    gray: '#4C5454',
    red: '#7C1227',
    statusNotStarted: '#DC3545',
    statusInProgress: '#F4D03F',
    statusCompleted: '#198754'
  };

  // Process recommendations into gantt chart format
  useEffect(() => {
    if (recommendations && recommendations.length > 0) {
      processRecommendationsForGantt();
    }
  }, [recommendations, showCompleted]);

  const processRecommendationsForGantt = () => {
    // Flatten recommendations and add category info
    const flattenedRecommendations = recommendations.reduce((acc, doc) => {
      const categoryRecommendations = doc.recommendations.map(rec => {
        // Make sure we're using targetDate as dueDate if it exists
        const dueDate = rec.targetDate || rec.dueDate || null;
        
        return {
          ...rec,
          dueDate,
          category: doc.category,
          assessmentName: doc.assessmentName,
          documentId: doc._id
        };
      });
      
      return [...acc, ...categoryRecommendations];
    }, []);
    
    // Filter out completed tasks if not showing them
    const filteredRecommendations = showCompleted 
      ? flattenedRecommendations 
      : flattenedRecommendations.filter(rec => rec.status !== 'Completed');
    
    // Sort by due date (null dates at the end)
    const sortedRecommendations = filteredRecommendations.sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    });
    
    // Calculate date range for the chart
    if (sortedRecommendations.length > 0) {
      const dates = sortedRecommendations
        .filter(rec => rec.dueDate)
        .map(rec => new Date(rec.dueDate));
      
      if (dates.length > 0) {
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        
        // Add buffer to start and end dates
        minDate.setDate(minDate.getDate() - 14);
        maxDate.setDate(maxDate.getDate() + 14);
        
        setTimeRange({
          startDate: minDate,
          endDate: maxDate
        });
      }
    }
    
    setGanttData(sortedRecommendations);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Not Started':
        return colors.statusNotStarted;
      case 'In Progress':
        return colors.statusInProgress;
      case 'Completed':
        return colors.statusCompleted;
      default:
        return colors.gray;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return colors.red;
      case 'High':
        return '#E67E22'; // Orange
      case 'Medium':
        return colors.secondary;
      case 'Low':
        return colors.statusCompleted;
      default:
        return colors.gray;
    }
  };

  // Calculate position and width for gantt bar
  const calculateBarPosition = (dueDate) => {
    if (!dueDate) return { left: '90%', width: '10%' };
    
    const date = new Date(dueDate);
    const totalDays = (timeRange.endDate - timeRange.startDate) / (1000 * 60 * 60 * 24);
    const daysFromStart = (date - timeRange.startDate) / (1000 * 60 * 60 * 24);
    
    const position = (daysFromStart / totalDays) * 100;
    
    return {
      left: `${Math.min(Math.max(position, 0), 100)}%`,
      width: '10px'
    };
  };

  // Calculate position for today marker
  const calculateTodayPosition = () => {
    const today = new Date();
    const totalDays = (timeRange.endDate - timeRange.startDate) / (1000 * 60 * 60 * 24);
    const daysFromStart = (today - timeRange.startDate) / (1000 * 60 * 60 * 24);
    
    const position = (daysFromStart / totalDays) * 100;
    
    return {
      left: `${Math.min(Math.max(position, 0), 100)}%`
    };
  };

  // Generate month labels for the timeline
  const generateMonthLabels = () => {
    const months = [];
    let currentDate = new Date(timeRange.startDate);
    
    while (currentDate <= timeRange.endDate) {
      months.push({
        date: new Date(currentDate),
        label: currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      });
      
      // Move to next month
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return months;
  };

  // Calculate position for month label
  const calculateMonthPosition = (date) => {
    const totalDays = (timeRange.endDate - timeRange.startDate) / (1000 * 60 * 60 * 24);
    const daysFromStart = (date - timeRange.startDate) / (1000 * 60 * 60 * 24);
    
    const position = (daysFromStart / totalDays) * 100;
    
    return {
      left: `${Math.min(Math.max(position, 0), 100)}%`
    };
  };

  // Export as image
  const exportAsImage = async () => {
    const element = chartRef.current;
    if (!element) return;
    
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      const imageData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imageData;
      link.download = `${organizationName || 'Organization'}_Roadmap_Gantt_Chart.png`;
      link.click();
    } catch (error) {
      console.error('Error exporting chart as image:', error);
    }
  };

  // Export as PDF
  const exportAsPdf = async () => {
    const element = chartRef.current;
    if (!element) return;
    
    try {
      // Create PDF document
      const pdf = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Add title
      pdf.setFontSize(16);
      pdf.setTextColor(36, 52, 68); // #243444
      pdf.text('Business Resilience Navigator - Implementation Roadmap', 40, 40);
      pdf.setFontSize(12);
      pdf.text(organizationName || 'Your Organization', 40, 60);
      
      // Add date
      const today = new Date();
      pdf.setFontSize(10);
      pdf.setTextColor(76, 84, 84); // #4C5454
      pdf.text(`Generated: ${today.toLocaleDateString()}`, pageWidth - 150, 40);
      
      // Prepare data for table
      const tableData = ganttData.map(task => [
        task.text,
        task.category,
        task.priority || 'Medium',
        task.status || 'Not Started',
        task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No date'
      ]);
      
      // Add table with pagination options
      pdf.autoTable({
        startY: 80,
        head: [['Task', 'Category', 'Priority', 'Status', 'Due Date']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [36, 52, 68], // #243444
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 50 },
          3: { cellWidth: 70 },
          4: { cellWidth: 70 }
        },
        margin: { top: 80 },
        didDrawPage: (data) => {
          // Add header to each page
          pdf.setFontSize(14);
          pdf.setTextColor(36, 52, 68); // #243444
          pdf.text('Business Resilience Navigator - Implementation Roadmap', 40, 30);
          pdf.setFontSize(10);
          pdf.text(organizationName || 'Your Organization', 40, 45);
          pdf.text(`Generated: ${today.toLocaleDateString()}`, pageWidth - 150, 30);
        }
      });
      
      // Create a separate page for the Gantt chart
      pdf.addPage();
      
      // Add Gantt chart title
      pdf.setFontSize(14);
      pdf.setTextColor(36, 52, 68); // #243444
      pdf.text('Implementation Roadmap - Gantt Chart', 40, 40);
      
      // Capture the Gantt chart as an image
      const canvas = await html2canvas(element, {
        scale: 1.2,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit on page with proper scaling
      const imgWidth = pageWidth - 80;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // If the image is too tall for one page, split it across multiple pages
      if (imgHeight > pageHeight - 80) {
        // Calculate how many pages we need
        const pagesNeeded = Math.ceil(imgHeight / (pageHeight - 80));
        
        for (let i = 0; i < pagesNeeded; i++) {
          if (i > 0) pdf.addPage();
          
          // Add header to each page
          pdf.setFontSize(14);
          pdf.setTextColor(36, 52, 68); // #243444
          pdf.text('Implementation Roadmap - Gantt Chart (continued)', 40, 40);
          
          // Calculate which portion of the image to show on this page
          const sourceY = i * canvas.height / pagesNeeded;
          const sourceHeight = canvas.height / pagesNeeded;
          
          // Add the image portion
          pdf.addImage(
            imgData, 
            'PNG', 
            40, // x position
            60, // y position
            imgWidth, // width
            imgHeight / pagesNeeded, // height
            `gantt-${i}`, // alias
            'FAST', // compression
            0, // rotation
            sourceY / canvas.height * canvas.height, // source x
            sourceY, // source y
            canvas.width, // source width
            sourceHeight // source height
          );
        }
      } else {
        // Image fits on one page
        pdf.addImage(imgData, 'PNG', 40, 60, imgWidth, imgHeight);
      }
      
      // Save PDF
      pdf.save(`${organizationName || 'Organization'}_Roadmap_Gantt_Chart.pdf`);
    } catch (error) {
      console.error('Error exporting chart as PDF:', error);
    }
  };
  
  // Export as Excel
  const exportAsExcel = () => {
    try {
      // Prepare worksheet data
      const wsData = [
        ['Business Resilience Navigator - Implementation Roadmap'],
        [`Organization: ${organizationName || 'Your Organization'}`],
        [`Generated: ${new Date().toLocaleDateString()}`],
        [],
        ['Task', 'Category', 'Priority', 'Status', 'Due Date']
      ];
      
      // Add task data
      ganttData.forEach(task => {
        wsData.push([
          task.text,
          task.category,
          task.priority || 'Medium',
          task.status || 'Not Started',
          task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No date'
        ]);
      });
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths
      const colWidths = [{ wch: 50 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      ws['!cols'] = colWidths;
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Roadmap');
      
      // Save file
      XLSX.writeFile(wb, `${organizationName || 'Organization'}_Roadmap_Gantt_Chart.xlsx`);
    } catch (error) {
      console.error('Error exporting chart as Excel:', error);
    }
  };

  // Toggle modal
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen && recommendations && recommendations.length > 0) {
      processRecommendationsForGantt();
    }
  };

  // Modal component
  const GanttChartModal = () => {
    if (!isModalOpen) return null;
    
    const monthLabels = generateMonthLabels();
    
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Implementation Roadmap - Gantt Chart</h2>
            <div style={styles.modalControls}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={() => setShowCompleted(!showCompleted)}
                  style={styles.checkbox}
                />
                Show Completed Tasks
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={exportAsImage} style={styles.exportButton}>
                  <FaFileExport /> Export as Image
                </button>
                <button onClick={exportAsExcel} style={styles.exportButton}>
                  <FaFileExport /> Export as Excel
                </button>
              </div>
              <button 
                onClick={toggleModal}
                style={styles.closeButton}
              >
                ✕
              </button>
            </div>
          </div>
          
          <div style={styles.ganttContainer} ref={chartRef} id="roadmap-gantt-chart">
            <div style={styles.ganttHeader}>
              <div style={styles.ganttHeaderTitle}>
                <h3 style={{ margin: 0 }}>Business Resilience Navigator</h3>
                <p style={{ margin: '5px 0 0 0' }}>{organizationName || 'Your Organization'}</p>
              </div>
              <div style={styles.timelineHeader}>
                {monthLabels.map((month, index) => (
                  <div 
                    key={index}
                    style={{
                      ...styles.monthLabel,
                      ...calculateMonthPosition(month.date)
                    }}
                  >
                    {month.label}
                  </div>
                ))}
              </div>
            </div>
            
            <div style={styles.ganttBody}>
              
              {/* Tasks with their markers */}
              {ganttData.map((task, index) => (
                <div key={index} style={styles.taskRow}>
                  <div style={styles.taskInfo}>
                    <div style={styles.taskTitle}>{task.text}</div>
                    <div style={styles.taskMeta}>
                      <span 
                        style={{
                          ...styles.priorityBadge,
                          backgroundColor: getPriorityColor(task.priority)
                        }}
                      >
                        {task.priority || 'Medium'}
                      </span>
                      <span 
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: getStatusColor(task.status)
                        }}
                      >
                        {task.status || 'Not Started'}
                      </span>
                      <span style={styles.taskCategory}>{task.category}</span>
                    </div>
                  </div>
                  
                  <div style={styles.taskTimeline}>
                    {/* Today marker for this row */}
                    {index === 0 && (
                      <div 
                        style={{
                          ...styles.todayMarker,
                          ...calculateTodayPosition()
                        }}
                      >
                        <div style={styles.todayLine}></div>
                        <div style={styles.todayLabel}>Today</div>
                      </div>
                    )}
                    <div 
                      style={{
                        ...styles.taskMarker,
                        ...calculateBarPosition(task.dueDate),
                        backgroundColor: getStatusColor(task.status)
                      }}
                      title={`${task.text} - Due: ${task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'Not set'}`}
                    >
                      <div style={styles.taskDot}></div>
                      <div style={styles.taskDate}>
                        {task.dueDate ? new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No date'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <button 
        onClick={toggleModal} 
        style={{
          backgroundColor: colors.secondary,
          color: colors.primary,
          border: 'none',
          borderRadius: '4px',
          padding: '8px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px'
        }}
      >
        <span style={{ fontSize: '16px' }}>📊</span>
        View Gantt Chart
      </button>
      <GanttChartModal />
    </>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#FFFAFA',
    borderRadius: '5px',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
    width: '90%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative'
  },
  modalHeader: {
    padding: '15px 20px',
    borderBottom: '1px solid #dee2e6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFFAFA',
    zIndex: 10
  },
  modalTitle: {
    margin: 0,
    color: '#243444',
    fontSize: '20px'
  },
  modalControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px',
    color: '#4C5454'
  },
  checkbox: {
    cursor: 'pointer'
  },
  exportButton: {
    backgroundColor: '#243444',
    color: '#FFFAFA',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#4C5454',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '50%'
  },
  ganttContainer: {
    padding: '20px',
    backgroundColor: '#FFFAFA'
  },
  ganttHeader: {
    marginBottom: '30px'
  },
  ganttHeaderTitle: {
    marginBottom: '20px',
    color: '#243444'
  },
  timelineHeader: {
    position: 'relative',
    height: '30px',
    borderBottom: '1px solid #dee2e6',
    marginLeft: '300px'
  },
  monthLabel: {
    position: 'absolute',
    fontSize: '12px',
    color: '#4C5454',
    transform: 'translateX(-50%)',
    textAlign: 'center'
  },
  ganttBody: {
    position: 'relative'
  },
  taskRow: {
    display: 'flex',
    borderBottom: '1px solid #f0f0f0',
    minHeight: '60px',
    alignItems: 'center'
  },
  taskInfo: {
    width: '300px',
    flexShrink: 0,
    padding: '10px 10px 10px 0',
    borderRight: '1px solid #dee2e6'
  },
  taskTimeline: {
    flex: 1,
    position: 'relative',
    height: '60px'
  },
  taskTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#243444',
    marginBottom: '5px'
  },
  taskMeta: {
    display: 'flex',
    gap: '5px',
    flexWrap: 'wrap'
  },
  priorityBadge: {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: '3px',
    color: '#FFFAFA',
    fontSize: '10px',
    fontWeight: 'bold'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: '3px',
    color: '#FFFAFA',
    fontSize: '10px',
    fontWeight: 'bold'
  },
  taskCategory: {
    fontSize: '10px',
    color: '#6c757d',
    backgroundColor: '#f8f9fa',
    padding: '2px 6px',
    borderRadius: '3px'
  },
  todayMarker: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '2px',
    zIndex: 5
  },
  todayMarker: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '2px',
    zIndex: 5
  },
  todayLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '2px',
    backgroundColor: '#243444',
    zIndex: 5
  },
  todayLabel: {
    position: 'absolute',
    top: '-25px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#243444',
    color: '#FFFAFA',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    whiteSpace: 'nowrap'
  },
  taskMarker: {
    position: 'absolute',
    height: '30px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    top: '15px' // Center vertically in the task row
  },
  taskDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#FFFAFA',
    border: '2px solid currentColor'
  },
  taskDate: {
    fontSize: '10px',
    color: '#FFFAFA',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: '2px 4px',
    borderRadius: '2px',
    marginTop: '2px',
    whiteSpace: 'nowrap'
  }
};

export default RoadmapGanttChart;
